import React, { useMemo } from 'react';
import { Bubble, Tag } from 'tea-component';
import { openWindowUrl } from '@/pages/opinion/utils/url';
import './index.scss';
import { getMonitoringSource } from '../TableCommonHook/utils';
import {
  MonitorSourceItemType,
  AccountCategoryType,
} from '../TableCommonHook/types';
import useLanguage from '@op/hooks/useLanguage';

export default function KeywordAccount({
  monitor_source,
  className,
}: {
  monitor_source: Array<MonitorSourceItemType>;
  className?: string;
  isMin?: boolean;
}) {
  const { formatMessage, isEn } = useLanguage(); 

  const { keyword, external, accountCategories, list } = useMemo(() => {
    const data = getMonitoringSource(monitor_source);

    return data;
  }, [monitor_source]);

  function openUrl(url: string) {
    openWindowUrl(url);
  }

  const getItemElement = (
    item: MonitorSourceItemType,
    isLast: boolean,
    key: string,
  ) => {
    const text =
      item.type?.toLocaleUpperCase?.() === 'DISCORD-PRIVATE'
        ? 'PRIVATE'
        : `${item.type}`;

    const typeText = item.type && `(${text})`;
    const isKeyword = key !== 'account';
    if (isKeyword) {
      return (
        <span className="keyword-item" key={item.name}>
          {item.name} {typeText} {isLast ? '' : ', '}
        </span>
      );
    }
    return (
      <span
        key={item.name}
        className="dbo-tablecommon-line--link"
        onClick={() => openUrl(item.url)}
      >
        {item.name} {typeText}
      </span>
    );
  };

  const getKeywordBubbleContent = () => {
    const ele = [];
    if (keyword.length > 0) {
      ele.push(<p key={'keyword'}>{formatMessage({ id: 'by Keyword' })}</p>);
      ele.push(
        <span key={'keyword-content'} className="dbo-bubble-line-keyword">
          {keyword.map((item, index) =>
            getItemElement(item, index === keyword.length - 1, 'keyword'),
          )}
        </span>,
      );
    }

    return ele.length === 0 ? null : ele;
  };

  const renderCategorySection = (category: AccountCategoryType) => {
    const { list: items, textInfo } = category;
    if (items.length === 0) return null;

    const title = isEn ? textInfo.text : textInfo.textCN;

    return (
      <React.Fragment key={category.key}>
        <p className="title">{title}</p>
        <p>
          {items.map((item, index) => (
            <p key={item.name}>
              {getItemElement(item, index === items.length - 1, 'account')}
            </p>
          ))}
        </p>
      </React.Fragment>
    );
  };

  const getExternalBubbleContent = () => {
    const ele = [];
    if (external.length > 0) {
      ele.push(
        <p key={'external'}>
          {formatMessage({ id: 'by External Data Sources' })}
        </p>,
      );
      ele.push(
        <span key={'external-content'} className="dbo-bubble-line-keyword">
          {external.map((item, index) =>
            getItemElement(item, index === external.length - 1, 'external'),
          )}
        </span>,
      );
    }

    return ele.length === 0 ? null : ele;
  };

  const getBubbleContent = () => {
    const keywordContent = getKeywordBubbleContent();
    const externalContent = getExternalBubbleContent();

    const accountContent = accountCategories
      .map((category) => renderCategorySection(category))
      .filter(Boolean);

    if (
      keywordContent === null &&
      accountContent?.length === 0 &&
      externalContent === null
    )
      return null;

    return (
      <>
        {keywordContent}
        {accountContent}
        {externalContent}
      </>
    );
  };

  if (!Array.isArray(list) || list.length === 0) {
    return null;
  }

  return (
    <div className={`${className} monitor-source-wrapper`}>
      {list.length > 0 && (
        <div className="monitor-source-item">
          <div className="text-content-info">
            <span className="monitor-title">
              {formatMessage({ id: 'Monitoring Source' })}:
            </span>
            <Bubble
              dark
              closeOnScroll
              content={list.length === 1 ? getBubbleContent() : null}
              overlayClassName="dbo-monitoring-show"
            >
              {list.map((item, index) =>
                getItemElement(item, index === list.length - 1, item.key),
              )}
            </Bubble>
          </div>
          {list.length > 1 && (
            <Bubble
              dark
              closeOnScroll
              content={getBubbleContent()}
              overlayClassName="dbo-monitoring-show"
            >
              <Tag className="tag-info">{list.length}</Tag>
            </Bubble>
          )}
        </div>
      )}
    </div>
  );
}
